import {Box} from '@mui/material';
import {FC, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {useParams} from 'react-router-dom';
import {ocppApi} from 'src/api';
import {PageHeader} from 'src/components/common/PageHeader';
import {useAppDispatch, useAppSelector} from 'src/app/hooks';
import {AppDispatch} from 'src/app/store';
import {ConfigurationsList} from 'src/components/configurations/ConfigurationsList';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {ChargePointByIdRequest, ChargePointSettingDTO, CONFIGURATIONS_TYPE, FormKeyValue,} from 'src/types';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';

type ChargerConfigurationsQueryTrigger = ReturnType<
    typeof ocppApi.useLazyGetConfigurationsQuery
>[0];

const handleGetChargerConfigurations =
    (trigger: ChargerConfigurationsQueryTrigger, dispatch: AppDispatch) =>
        async (data: ChargePointByIdRequest) => {
            dispatch(preloaderChangeStatus(true));

            try {
                return await trigger(data).unwrap();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

export const ChargerConfigurations: FC = () => {
    const {id} = useParams();
    const {activeBusiness} = useAppSelector((state) => state.common);

    const intl = useIntl();
    const dispatch = useAppDispatch();
    const {CONFIGURATIONS}: { CONFIGURATIONS: CONFIGURATIONS_TYPE[] } = useAppSelector(
        (state) => state.common
    );

    const [trigger, response] = ocppApi.useLazyGetConfigurationsQuery();

    const [configurations, setConfigurations] = useState<ChargePointSettingDTO[]>();

    useEffect(() => {
        id &&
        void handleGetChargerConfigurations(
            trigger,
            dispatch
        )({chargerId: +id, businessId: activeBusiness});
    }, []);

    useEffect(() => {
        if (configurations && id) {
            const incoming = CONFIGURATIONS.find((conf) => conf.chargerId === +id);
            let changed = 0;
            const data = configurations.map((item) => {
                const newValue = incoming?.configurations?.find(
                    (conf: FormKeyValue) => conf.key === item.key
                );
                if (newValue && newValue.value !== item.value) {
                    changed += 1;
                    return {
                        ...item,
                        value: newValue.value,
                    };
                }
                return item;
            });
            if (changed > 0) {
                setConfigurations(data);
            }
        }
    }, [CONFIGURATIONS]);

    useEffect(() => {
        if (response.data) {
            // Backend returns configs in a non-deterministic order, so rows (incl. the edited
            // one) jumped around on every refetch — sort by key for a stable list order
            setConfigurations([...response.data].sort((a, b) => a.key.localeCompare(b.key)));
        }
    }, [response]);

    return (
        <Box>
            <PageHeader
                title={intl.formatMessage({id: 'configurations'})}
                onlyTitle
            />
            {configurations && <ConfigurationsList data={configurations}/>}
        </Box>
    );
};
